/**
 * mailRegionsExtension — NgwEditor 의 4-region mail 도메인 extension (PR-N4).
 *
 * PoC (`apps/owma/src/__poc__/ngweditor/extensions/mail-regions.ts`) 를 본 위치로 이전.
 * 이전 시 추가된 사항:
 *   1. legacy TinyMCE DOM 마커 parseDOM rule 보강 — PR-N7 의 feature flag 전환 직후
 *      기존 답장 임시저장/답장 HTML 이 4-region 노드로 정확히 복원되도록.
 *   2. NgwExtension / NodeSpec / NodeViewFactory 타입 정합 (`any` 0건).
 *   3. PoC `insertMailLayout` 명령 유지 — quotedButton install 콜백이 lazy 삽입 경로로
 *      사용한다 (NgwEditorQuotedButton 참조).
 *
 * ### 4-region 매핑 (03 §2.4 IEditorMailRegion ↔ ProseMirror node)
 *
 * ngwEditor 0.2.11 가이드 §8.1 형태로 toDOM 클래스 일원화 — REGION_CLASSES 단일 source of truth.
 *
 * | IRegion         | ProseMirror node | toDOM 클래스 (REGION_CLASSES) | parseDOM 호환                                        |
 * | --------------- | ---------------- | ----------------------------- | ---------------------------------------------------- |
 * | nBody           | `nbody`          | `nine_body`                   | `div.nine_body` / `div[id^="nine_body_"]` / 신규 PoC |
 * | signature       | `signature`      | `nine_signature`              | `div.nine_signature` / `div[id^="nine-sign-"]`       |
 * | quotedHeader    | `quoted_header`  | `quoted_header_editor`        | `div.quoted_header_editor`                           |
 * | quotedBody      | `quoted_body`    | `quoted_body_editor`          | `div.quoted_body_editor` (fold ↔ collapsed)          |
 *
 * legacy 마커 출처 (apps/owma/public/editor/nine_tinymce_desktop.js):
 *   - L6266 `editorMgr.nineBodyID = 'nine_body_' + ...` + L6332 `addClass('nine_body')`
 *   - L6059 `editorMgr.nineSignID = 'nine-sign-' + ...` + L6111 `addClass('nine_signature')`
 *   - L6627 `addClass('quoted_header_editor')`
 *   - L6737 `addClass('quoted_body_editor')`
 *
 * 추가 mail-sample 호환:
 *   - PoC 가 일반화하기 전 mail-sample (`signature.ts` / `email-quote.ts`) 의 `signature_block` /
 *     `email_original` 노드 마커. mail-sample 자체 데이터셋의 답장 HTML 도 그대로 인식.
 *
 * ### quoted_body 의 collapsed attr
 *
 * `data-collapsed="true" | "false"` 만 보존. NgwEditorQuotedButton.install 의 lazy 삽입 콜백은
 * 본 attr 의 토글과 독립 — install 은 "원문 영역 자체를 doc 에 lazy 추가" 의 시맨틱, collapsed 는
 * "이미 doc 에 있는 quoted_body 의 보기/숨기기" 의 시맨틱. PoC NodeView 의 ··· 토글은 collapsed
 * 만 토글하며, install/skip 플래그는 NgwEditorMailExtensions 의 어댑터 멤버에서 별도 관리.
 *
 * ### C-1 (editor-collapsed-region-guard): 접힌(collapsed) 영역 커서 진입·편집 가드
 *
 * collapsed=true(CSS `display:none`) 인 quoted_header/quoted_body 는 화면에서만 숨겨질 뿐 PM doc
 * 모델에는 유효 커서 위치로 살아있어, 방향키로 진입 후 paste/입력이 숨은 영역에 반영되는 버그가
 * 있었다. `setup(ctx)` 에 collapsedGuardPlugin 을 주입해 두 층위로 방어한다:
 *   - filterTransaction: 내용변경 없는 selection-only 트랜잭션이 collapsed 로 진입하면 거부(진입 차단).
 *   - appendTransaction: docChanged(init/토글) 후 커서가 collapsed 에 놓이면 "마지막 비-collapsed
 *     region 내부"로 복귀. 연속 collapsed(quoted_header+body) 대비 innermost before(d) 대신
 *     doc 직속 region 순회로 복귀 대상 결정. (openspec/changes/editor-collapsed-region-guard 참조)
 *
 * ### 보안 — XSS
 *
 * `setHTML` 경로는 항상 NgwEditor 의 ProseMirror parser 가 schema 기반 필터링.
 * 본 extension 의 `insertMailLayout` 명령 안에서 사용하는 브라우저 `DOMParser` 는
 * `<script>` 비활성 / `<iframe>` 무력화 + ProseMirror `parser.parse` 가 추가로 schema 외 노드
 * 필터링. 4-region 의 parseDOM 도 `tag` 셀렉터만 사용 — getAttrs 콜백 안에서 attribute 만 보존.
 */

import {
  // `Selection` / `ResolvedPos` / `Transaction` / `EditorState` 는 C-1(collapsed 가드)에서 사용.
  // `Fragment` / `Slice` 는 D-1(region-clipboard-unwrap)의 runtime 사용으로 type-only 해제.
  DOMParser as PMDOMParser,
  EditorState,
  Fragment,
  removeLoneBrs,
  Selection,
  Slice,
  type EditorView,
  type NgwExtension,
  // 전역 DOM `Node` 와 충돌하므로 ProseMirror Node 는 PMNode 로 alias.
  type Node as PMNode,
  type NodeViewFactory,
  type Plugin,
  type ResolvedPos,
  type Transaction,
} from '@ngweditor/react'
import { isDev } from '../editorEnv'

/**
 * A-1.fix (ngwEditor-migration-0.2.12): PMDOMParser.parse 가 doc.content 시퀀스 강제
 * (`nbody blank_sign? ...`)를 만족시키기 위해 외부 paragraph 를 nbody 안에 자동 wrap 하는
 * 부작용 처리.
 *
 * 동작: parsed.content 가 단일 자식 `nbody` 노드만 갖고 있으면 그 내부 content 를 추출. 외부
 * 입력이 이미 명시적으로 `<div class="nine_body">...</div>` 였다면 nbody 가 둘 이상 또는 다른
 * region 노드와 혼재할 가능성 — 그 경우 의도된 구조라 unwrap 안 함.
 *
 * region.create(attrs, content) 의 content 에 들어가기 전 호출하여 nested nbody 차단.
 */
export function unwrapAutoNbody(content: Fragment): Fragment {
  if (content.childCount === 1 && content.firstChild?.type.name === 'nbody') {
    return content.firstChild.content
  }
  return content
}

/** 6-region 노드 이름 집합. region 은 doc 직속 전용이라 다른 region 의 자식이 될 수 없다. */
const REGION_NODE_NAMES: ReadonlySet<string> = new Set([
  'nbody',
  'blank_sign',
  'signature',
  'blank_msg',
  'quoted_header',
  'quoted_body',
])

/**
 * region 노드가 다른 region 의 content 로 nested 되지 않도록 최상위 region 노드를 재귀적으로
 * 펼쳐 순수 block sequence 로 만든다.
 *
 * 배경: 각 region 의 content spec 은 `block+` 이라 자식으로 region 노드를 받을 수 없다(region 은
 * doc 직속 전용). 그러나 4-region 구조로 저장/발송된 메일을 인용(quoted_body)할 때 그 원문 HTML 을
 * htmlToContainer + parser.parse 로 파싱하면 doc.content 매처가 `nine_body` / `blank_sign` 등을
 * doc 직속 region 으로 파싱해 `parsed.content = [nbody, blank_sign, signature, ...]` 형태가 된다.
 * `unwrapAutoNbody` 는 **단일** nbody wrap 만 풀어 이 복합 케이스를 처리하지 못하므로, 그 결과를
 * region.create 에 그대로 넣으면 `quoted_body(nbody, blank_sign, ...)` 같은 스키마 위반 노드가
 * 생성된다. create 는 검증하지 않아 doc 에 들어가지만, 이후 collapsed 토글의 **`setNodeMarkup`**
 * (노드를 재생성하며 content 를 재검증)이 "Invalid content for node quoted_body" RangeError 로
 * 크래시한다. 토글 버튼 경로는 `setNodeAttribute`(AttrStep, content 무검증)로 이를 회피하지만
 * (아래 참조), 배치 펼침(`buildQuotedCollapseTransaction`)은 여전히 `setNodeMarkup` 을 쓰므로
 * 이 방어가 필요하다. 최상위에 나타난 region 노드를 재귀적으로 펼쳐 크래시를 차단한다.
 *
 * region 노드가 하나도 없는 정상 content 는 동일한 block 배열을 그대로 돌려준다(무해).
 */
export function flattenNestedRegions(content: Fragment): PMNode[] {
  const out: PMNode[] = []
  for (let i = 0; i < content.childCount; i++) {
    const child = content.child(i)
    if (REGION_NODE_NAMES.has(child.type.name)) {
      out.push(...flattenNestedRegions(child.content))
    } else {
      out.push(child)
    }
  }
  return out
}

/**
 * 4-region 의 toDOM / NodeView / DOM_SELECTOR (NgwEditorMailRegion) 가 공유하는 클래스명.
 * 본 const 가 단일 source of truth — 변경 시 3곳 동시 갱신 강제.
 *
 * legacy TinyMCE 의 `mceEditable` 클래스는 TinyMCE 의 contenteditable 토글 메커니즘 전용이라
 * NgwEditor 출력에는 부여하지 않는다. 외부 입력 (legacy 답장/임시저장 HTML) 에 그 클래스가
 * 섞여 있어도 parseDOM 셀렉터는 `nine_body` / `nine_signature` / `quoted_*_editor` 만 보므로
 * 그대로 받아들인다.
 */
export const REGION_CLASSES = {
  nbody: 'nine_body',
  blankSign: 'blank sign',
  signature: 'nine_signature',
  blankMsg: 'blank msg',
  quotedHeader: 'quoted_header_editor',
  quotedBody: 'quoted_body_editor',
} as const

/**
 * blank_sign / blank_msg 출력용 className. 식별용 `REGION_CLASSES.blank*` 에 legacy/모바일
 * 호환을 위한 `nine-pg` 토큰을 prepend 한 형태. PM toDOM, absorbBlankRegion className 정규화,
 * normalizeMultipleBlankSignsInNineBody 의 keeper className 통일 시 사용한다.
 *
 * 식별 (isRegionDiv / parseDOM) 은 `REGION_CLASSES` 의 필수 토큰 기준 — 입력에 `nine-pg` 가
 * 없는 legacy 데이터도 정상 인식.
 */
export const BLANK_SIGN_OUTPUT_CLASS = `nine-pg ${REGION_CLASSES.blankSign}`
export const BLANK_MSG_OUTPUT_CLASS = `nine-pg ${REGION_CLASSES.blankMsg}`

/** 6-region div 의 id 접두사. class 가 유실된 입력 (외부 메일 클라이언트) 대비 식별 보조. */
const REGION_ID_PREFIXES = [
  'nine_body_',
  'blank_sign_',
  'nine-sign-',
  'blank_msg_',
  'quoted_header_',
  'quoted_body_',
] as const

/**
 * element 가 6-region (nbody / blank_sign / signature / blank_msg / quoted_header / quoted_body)
 * div 인지 판정.
 *
 * `wrapEmailBodyHTML` / `unwrapEmailBodyHTML` 가 외곽 wrapper 후보를 결정할 때 `div[nine-mail-id]`
 * 셀렉터 매치 결과에서 region div 를 제외하기 위해 사용한다.
 *
 * blank 의 REGION_CLASSES 값은 두 토큰('blank sign' / 'blank msg')이므로, `classList.contains`
 * 에 공백이 든 인자를 직접 넘기면 DOMException 이 발생한다. 토큰 분해 후 전부 포함하는지 검사.
 */
export function isRegionDiv(el: Element): boolean {
  for (const cls of Object.values(REGION_CLASSES)) {
    const tokens = cls.split(' ')
    if (tokens.every((t) => el.classList.contains(t))) return true
  }
  const id = el.getAttribute('id') ?? ''
  return REGION_ID_PREFIXES.some((prefix) => id.startsWith(prefix))
}

/**
 * HTML 단편을 ProseMirror parser 가 받을 수 있는 DOM 요소로 변환.
 * 브라우저 `DOMParser.parseFromString('<div>...</div>', 'text/html')` 는 자동 정제.
 *
 * 0.2.29: `insertMailLayout` 이 각 region(nbody/signature/quotedHeader/quotedBody) 의 외부 메일 HTML 을
 * PMDOMParser 로 직접 파싱해 코어 setHTML 후처리를 우회하므로, 높이 유지용 단독 `<br>`(외부 메일의
 * `<div><br></div>` 관례)이 hard_break 로 파싱돼 빈 줄이 두 줄로 벌어지지 않도록 `removeLoneBrs` 를
 * 파싱 직전 적용. `<div>텍스트<br></div>` / `<div><br><br></div>` 등 의도된 줄바꿈은 보존.
 */
function htmlToContainer(html: string): HTMLDivElement {
  const fragment = `<div id="ngw-mail-region-container">${html || '<p></p>'}</div>`
  const browserDoc = new DOMParser().parseFromString(fragment, 'text/html')
  const container = browserDoc.getElementById('ngw-mail-region-container') as HTMLDivElement | null
  if (container == null) return document.createElement('div')
  removeLoneBrs(container)
  return container
}

/**
 * legacy 마커 인식용 parseDOM rule 빌더.
 *
 * 신규 NgwEditor 4-region 마커 + legacy TinyMCE/mail-sample 마커 모두 한 nodeSpec.parseDOM 의
 * rule 배열로 노출. PM parser 가 우선순위 순서대로 매칭한다.
 */
const NBODY_PARSE_DOM: ReadonlyArray<{
  tag: string
  getAttrs?: (node: HTMLElement | string) => Record<string, unknown> | false | null
}> = [
  // 신규 마커 (PoC)
  { tag: 'div.nine-region.nine-region-nbody' },
  // legacy TinyMCE (nine_tinymce_desktop.js L6266/L6332)
  { tag: 'div.nine_body' },
  { tag: 'div[id^="nine_body_"]' },
]

const SIGNATURE_PARSE_DOM: ReadonlyArray<{
  tag: string
  getAttrs?: (node: HTMLElement | string) => Record<string, unknown> | false | null
}> = [
  // 신규 마커 (PoC)
  { tag: 'div.nine-region.nine-region-signature' },
  // legacy TinyMCE (nine_tinymce_desktop.js L6059/L6111)
  { tag: 'div.nine_signature' },
  { tag: 'div[id^="nine-sign-"]' },
  // mail-sample 의 signature_block (PoC 일반화 전)
  { tag: 'div.signature_block' },
  { tag: 'div[data-mail-region="signature_block"]' },
]

const QUOTED_HEADER_PARSE_DOM: ReadonlyArray<{
  tag: string
  getAttrs?: (node: HTMLElement | string) => Record<string, unknown> | false | null
}> = [
  // 신규 마커 (PoC)
  {
    tag: 'div.nine-region.nine-region-quoted-header',
    getAttrs: (dom) => {
      if (typeof dom === 'string') return false
      return { collapsed: dom.classList.contains('fold') }
    },
  },
  // legacy TinyMCE (nine_tinymce_desktop.js L6627). mail-adapter-migration §8.1 참조 —
  // `fold` 클래스 ↔ `collapsed` attr 라운드트립 매핑.
  {
    tag: 'div.quoted_header_editor',
    getAttrs: (dom) => {
      if (typeof dom === 'string') return false
      return { collapsed: dom.classList.contains('fold') }
    },
  },
]

const QUOTED_BODY_PARSE_DOM: ReadonlyArray<{
  tag: string
  getAttrs?: (node: HTMLElement | string) => Record<string, unknown> | false | null
}> = [
  // 신규 마커 (0.2.11) — fold 클래스로 collapsed 매핑.
  {
    tag: 'div.quoted_body_editor',
    getAttrs: (dom) => {
      if (typeof dom === 'string') return false
      return { collapsed: dom.classList.contains('fold') }
    },
  },
  // legacy PoC 마커 (data-collapsed 보존) — mid-migration HTML
  {
    tag: 'div.nine-region.nine-region-quoted-body',
    getAttrs: (dom) => {
      if (typeof dom === 'string') return false
      return { collapsed: dom.getAttribute('data-collapsed') !== 'false' }
    },
  },
  // mail-sample 의 email_original (PoC 일반화 전)
  {
    tag: 'div.email_original',
    getAttrs: (dom) => {
      if (typeof dom === 'string') return false
      return {
        collapsed: dom.getAttribute('data-collapsed') !== 'false',
      }
    },
  },
]

const BLANK_SIGN_PARSE_DOM: ReadonlyArray<{
  tag: string
  getAttrs?: (node: HTMLElement | string) => Record<string, unknown> | false | null
}> = [{ tag: 'div.blank.sign' }, { tag: 'div[id^="blank_sign_"]' }]

const BLANK_MSG_PARSE_DOM: ReadonlyArray<{
  tag: string
  getAttrs?: (node: HTMLElement | string) => Record<string, unknown> | false | null
}> = [{ tag: 'div.blank.msg' }, { tag: 'div[id^="blank_msg_"]' }]

// ============================================================
/**
 * doc 에 존재하는 quoted_header / quoted_body 의 collapsed attr 를 일괄로 `collapsed` 값으로
 * 맞추는 transaction 을 만든다. setNodeMarkup 은 attrs 만 바꿔 노드 크기가 불변이므로 수집한
 * pos 가 같은 transaction 내에서 유효하다. 이미 원하는 상태이거나 quoted 영역이 없으면 `null`
 * (호출자가 dispatch 를 건너뜀). header/body 를 한 transaction 으로 동기화해 fold 정합을 유지.
 *
 * 현재 live 호출은 Host wrapper(`NgwMailEditor`)의 **재펼침**(collapsed=false) 경로뿐이다(서명 편집기
 * 등에서 내용 주입 시점마다 인용을 펼침) — content 의 fold 마커에 의존하지 않고 live doc 의 attr 을
 * 직접 강제한다. 메일 작성의 showOriginalMessage 초기 접힘은 이 함수가 아니라 `insertMailLayout` 의
 * `quotedCollapsed` 옵션(삽입 시점)에서 처리한다. 함수 자체는 collapsed 양방향을 지원한다.
 */
export function buildQuotedCollapseTransaction(
  state: EditorState,
  collapsed: boolean,
): Transaction | null {
  const targets: { pos: number; attrs: Record<string, unknown> }[] = []
  state.doc.descendants((node, pos) => {
    if (node.type.name === 'quoted_header' || node.type.name === 'quoted_body') {
      targets.push({ pos, attrs: node.attrs })
    }
    return true
  })
  if (targets.length === 0) return null

  let tr = state.tr
  for (const target of targets) {
    if (Boolean(target.attrs.collapsed) === collapsed) continue
    // setNodeMarkup 이 아닌 setNodeAttribute(AttrStep) — setNodeMarkup 은 attr 만 바꿔도 노드
    // content 를 스키마(`block+`)로 재검증해, 외부 메일 원문을 파싱한 quoted_body content 가
    // 매처를 완벽히 만족하지 않으면 RangeError 로 크래시한다(toggleCollapsed 와 동일 근거 —
    // lazy-collapsed-quoted-render 로 본 경로(프로그램 펼침)의 사용 빈도가 늘어 함께 정비).
    tr = tr.setNodeAttribute(target.pos, 'collapsed', collapsed)
  }
  return tr.docChanged ? tr : null
}

// ============================================================
// C-1 (editor-collapsed-region-guard): 접힌(collapsed) 영역 커서 진입·편집 가드
// ============================================================
//
// 아래 4개 함수는 mounted view 없이 단위 테스트 가능하도록 export 순수 함수로 분리한다.
// setup(ctx) 는 이들을 BundledPlugin spec 으로 감싸 주입한다.

/** ResolvedPos 조상 중 collapsed 인 quoted_header/quoted_body 의 depth. 없으면 -1. */
export function collapsedRegionDepth($pos: ResolvedPos): number {
  for (let d = $pos.depth; d > 0; d--) {
    const n = $pos.node(d)
    if (
      (n.type.name === 'quoted_header' || n.type.name === 'quoted_body') &&
      Boolean(n.attrs.collapsed)
    ) {
      return d
    }
  }
  return -1
}

/** selection 의 head/anchor 중 하나라도 collapsed region 내부이면 true. */
export function selectionInCollapsed(sel: Selection): boolean {
  try {
    return collapsedRegionDepth(sel.$head) >= 0 || collapsedRegionDepth(sel.$anchor) >= 0
  } catch {
    return false
  }
}

/**
 * 1차 방어 — 내용 변경 없는 selection-only 트랜잭션이 collapsed 로 진입하면 거부.
 * docChanged 트랜잭션(init/토글/paste)은 여기서 막지 않는다(2차 방어가 처리).
 */
export function collapsedGuardFilterTransaction(tr: Transaction): boolean {
  return !(!tr.docChanged && tr.selectionSet && selectionInCollapsed(tr.selection))
}

/**
 * 2차 방어 — docChanged 후 커서가 collapsed 에 놓이면 "마지막 비-collapsed region 내부"로 복귀.
 * ⚠️ innermost `$head.before(d)` 는 연속 collapsed(quoted_header+body)에서 앞의 collapsed 로
 *    들어가므로 금지. region 은 항상 doc 직속 자식이라 childCount/child(i) 로 순회 가능.
 */
export function collapsedGuardAppendTransaction(
  trs: ReadonlyArray<Transaction>,
  _oldState: EditorState,
  newState: EditorState,
): Transaction | null {
  if (!trs.some((tr) => tr.docChanged)) return null
  if (!selectionInCollapsed(newState.selection)) return null
  const { doc } = newState
  // ambient 에 Node.forEach 미선언 → childCount/child(i)(둘 다 typed)로 순회.
  // nbody 는 항상 비-collapsed 이므로 후보 최소 1개 보장.
  let visibleEnd: number | null = null
  let offset = 0
  for (let i = 0; i < doc.childCount; i++) {
    const child = doc.child(i)
    const collapsed =
      (child.type.name === 'quoted_header' || child.type.name === 'quoted_body') &&
      Boolean(child.attrs.collapsed)
    if (!collapsed) visibleEnd = offset + child.nodeSize - 1
    offset += child.nodeSize
  }
  if (visibleEnd == null) return null
  try {
    return newState.tr.setSelection(Selection.near(doc.resolve(visibleEnd), -1))
  } catch {
    return null
  }
}

// ============================================================
// D-1 (region-clipboard-unwrap): copy/cut/paste 클립보드 slice 의 region 노드 평탄화
// ============================================================

/**
 * slice 최상위의 region 노드를 내용물(children)로 unwrap 한 새 Slice 를 만든다.
 *
 * 배경: region 노드 6종은 doc 직속 고정 시퀀스(A-1 `doc.content`)에만 배치 가능해, region 이
 * 포함된 slice 를 답장 레이아웃의 nbody 안에 paste 하면 PM replace fitter 가 배치 실패로 paste
 * 전체를 조용히 no-op 처리한다. (재현: 에디터에서 인용 영역 복사 → 답장 본문에 붙여넣기)
 *
 * 적용 지점 (setup 의 clipboardRegionPlugin):
 *   - transformCopied — copy/cut/dragstart 의 serializeForClipboard 첫 단계에서 호출되어
 *     ① 직렬화 HTML 에서 region 마커(class/id)가 사라지고, ② 이후의 data-pm-slice context 기록
 *     루프가 unwrap 된 slice 를 보므로 region 이름이 context JSON 에 기록되어 paste 시
 *     addContext 로 재생성되는 숨은 경로도 함께 차단된다.
 *   - transformPasted — 외부 유입 방어 (수정 전 버전/다른 인스턴스가 복사해 둔 클립보드, 뷰어에서
 *     복사한 OfficeMail 발신 메일 HTML 등 region 마커가 남아 있는 입력).
 *
 * openStart/openEnd 보정: 열린(open) 첫/마지막 자식이 region 이면 unwrap 으로 depth 가 1 줄어
 * 각각 -1. 닫힌(open 0) 쪽은 그대로. 첫/마지막 자식이 region 이 아니면 보정하지 않는다
 * (transformPasted 입력은 외부 HTML 파싱 결과라 region/비-region 최상위 혼재 가능).
 */
export function unwrapRegionSlice(slice: Slice): Slice {
  const { content } = slice
  let hasRegion = false
  for (let i = 0; i < content.childCount; i++) {
    if (REGION_NODE_NAMES.has(content.child(i).type.name)) {
      hasRegion = true
      break
    }
  }
  if (!hasRegion) return slice

  const blocks: PMNode[] = []
  for (let i = 0; i < content.childCount; i++) {
    const node = content.child(i)
    if (REGION_NODE_NAMES.has(node.type.name)) {
      for (let j = 0; j < node.content.childCount; j++) blocks.push(node.content.child(j))
    } else {
      blocks.push(node)
    }
  }
  if (blocks.length === 0) return Slice.empty

  const firstIsRegion =
    content.firstChild != null && REGION_NODE_NAMES.has(content.firstChild.type.name)
  const lastIsRegion =
    content.lastChild != null && REGION_NODE_NAMES.has(content.lastChild.type.name)
  const openStart = firstIsRegion && slice.openStart > 0 ? slice.openStart - 1 : slice.openStart
  const openEnd = lastIsRegion && slice.openEnd > 0 ? slice.openEnd - 1 : slice.openEnd
  return new Slice(Fragment.fromArray(blocks), openStart, openEnd)
}

/**
 * mailRegionsExtension factory — nineMailId closure 캡처 (0.2.11 가이드 §8.1).
 *
 * 각 region 의 toDOM 이 본 nineMailId 를 id prefix 로 사용 (`nine_body_{nineMailId}` 등).
 * nineMailId 는 ngwEditor 도메인 식별자이며 JMAP emailId (`EmailObj.data.id`) 와는 별개.
 * 첫 글자 'n' prefix 는 legacy nine_tinymce_desktop 호환을 위해 강제.
 *
 * 라이프사이클: NgwEditorLifecycle.init() 안에서 1회 호출. extensions 배열은 init 동안 freeze.
 */
export interface MailRegionLabels {
  nbody: string
  signature: string
  quotedHeader: string
  quotedBody: string
  toggleQuoted: string
  hideQuoted: string
}

export const DEFAULT_MAIL_REGION_LABELS: MailRegionLabels = {
  nbody: '본문',
  signature: '서명',
  quotedHeader: '인용 헤더',
  quotedBody: '인용 원문',
  toggleQuoted: '원문 보기 / 숨기기',
  hideQuoted: '원문 숨기기',
}

export function createMailRegionsExtension(
  nineMailId: string,
  labels: MailRegionLabels = DEFAULT_MAIL_REGION_LABELS,
): NgwExtension {
  if (!nineMailId) {
    throw new Error('[mail-regions] nineMailId is required (예: "ncompose-{uuid}")')
  }
  return {
    name: 'mail-regions',

    nodes: {
      // A-1 (ngwEditor-migration-0.2.12): doc content 를 region 시퀀스로 강제. Cmd+A → Delete
      // 시 PM 의 schema validation + fillContent 가 nbody (필수) 를 자동 보강하여 region 전체
      // 소멸을 차단. nbody 만 필수, 나머지는 옵셔널. PoC P-A1 에서 schema-builder override 동작
      // 확인됨 (4/4 PASS).
      doc: { content: 'nbody blank_sign? signature? blank_msg? quoted_header? quoted_body?' },

      nbody: {
        // A-1.fix (ngwEditor-migration-0.2.12, 2026-05-28): `group: 'block'` 제거.
        //
        // 배경: 6 region 모두 `group: 'block'` + `content: 'block+'` 라면, region 의 content
        // 매처가 자기 자신을 포함한 region 들을 후보로 선택할 수 있다. 특히 A-3 의
        // `nbody.createAndFill()` 가 nbody.content('block+') 의 minimum 매칭으로 block 그룹의
        // 첫 NodeSpec(nbody 자기 자신)을 자식으로 채워 nested `<nbody><nbody>...</nbody></nbody>`
        // 가 생성. PM schema repair 도 동일 메커니즘으로 각 region 안에 nbody 가 한 겹씩 추가됨.
        //
        // 해결: region 은 doc 의 직속 자식만 되어야 하므로 block 그룹에서 제외. `doc.content`
        // 의 `'nbody blank_sign? ...'` 는 명시적 노드 이름 매칭이라 group 무관 → A-1 보호 유지.
        // region 의 'block+' content 는 코어 표준 block (paragraph/heading/table 등) 만 후보가
        // 되어 nested 자기-참조 차단.
        content: 'block+',
        selectable: false,
        defining: true,
        // G-8 (ngwEditor-migration-0.2.12, 2026-06-04): isolating 제거 — region 경계 Backspace
        // 병합 허용. 구 A-2 의 isolating(cross-delete 차단)은 nbody 외 region 의 정상 병합까지
        // 막았다. nbody 보호는 A-1(doc.content 'nbody …' 필수) + A-3(appendTransaction 재삽입)로
        // 승계되므로 isolating 없이도 nbody 생존 보장. (design §2 정책 전환 / tasks §G-8)
        attrs: {},
        parseDOM: NBODY_PARSE_DOM,
        toDOM: () => [
          'div',
          {
            id: `nine_body_${nineMailId}`,
            class: REGION_CLASSES.nbody,
            dir: 'auto',
            contenteditable: 'true',
            // a11y (ngwEditor-migration-0.2.12, Cycle 3): 스크린 리더 사용자에게 region 경계 의미 전달.
            role: 'region',
            'aria-label': labels.nbody,
          },
          0,
        ],
      },

      blank_sign: {
        // A-1.fix: group: 'block' 제거 (nbody 주석 참조)
        content: 'block+',
        selectable: false,
        defining: true,
        // G-8: isolating 제거 (nbody 주석 참조)
        attrs: {},
        parseDOM: BLANK_SIGN_PARSE_DOM,
        toDOM: () => [
          'div',
          {
            id: `blank_sign_${nineMailId}`,
            class: BLANK_SIGN_OUTPUT_CLASS,
            dir: 'auto',
            contenteditable: 'true',
          },
          0,
        ],
      },

      signature: {
        // A-1.fix: group: 'block' 제거 (nbody 주석 참조)
        content: 'block+',
        // G-8: selectable true→false (selectNodeBackward node-selection 엣지 제거), isolating 제거
        selectable: false,
        defining: true,
        attrs: {},
        parseDOM: SIGNATURE_PARSE_DOM,
        toDOM: () => [
          'div',
          {
            id: `nine-sign-${nineMailId}`,
            class: REGION_CLASSES.signature,
            dir: 'auto',
            contenteditable: 'true',
            // a11y (Cycle 3): nbody 주석 참조
            role: 'region',
            'aria-label': labels.signature,
          },
          0,
        ],
      },

      blank_msg: {
        // A-1.fix: group: 'block' 제거 (nbody 주석 참조)
        content: 'block+',
        selectable: false,
        defining: true,
        // G-8: isolating 제거 (nbody 주석 참조)
        attrs: {},
        parseDOM: BLANK_MSG_PARSE_DOM,
        toDOM: () => [
          'div',
          {
            id: `blank_msg_${nineMailId}`,
            class: BLANK_MSG_OUTPUT_CLASS,
            dir: 'auto',
            contenteditable: 'true',
          },
          0,
        ],
      },

      quoted_header: {
        // A-1.fix: group: 'block' 제거 (nbody 주석 참조)
        content: 'block+',
        selectable: false,
        defining: true,
        // G-8: isolating 제거 (nbody 주석 참조)
        attrs: {
          collapsed: { default: false },
        },
        parseDOM: QUOTED_HEADER_PARSE_DOM,
        toDOM: (node) => [
          'div',
          {
            id: `quoted_header_${nineMailId}`,
            class: `${REGION_CLASSES.quotedHeader}${node.attrs.collapsed ? ' fold' : ''}`,
            dir: 'auto',
            // a11y (Cycle 3): nbody 주석 참조
            role: 'region',
            'aria-label': labels.quotedHeader,
          },
          0,
        ],
      },

      quoted_body: {
        // A-1.fix: group: 'block' 제거 (nbody 주석 참조)
        content: 'block+',
        // G-8: selectable true→false (selectNodeBackward node-selection 엣지 제거), isolating 제거
        selectable: false,
        defining: true,
        attrs: {
          collapsed: { default: true },
        },
        parseDOM: QUOTED_BODY_PARSE_DOM,
        toDOM: (node) => {
          const attrs: Record<string, string> = {
            id: `quoted_body_${nineMailId}`,
            class: `${REGION_CLASSES.quotedBody}${node.attrs.collapsed ? ' fold' : ''}`,
            dir: 'auto',
            contenteditable: 'true',
            // a11y (Cycle 3): nbody 주석 참조
            role: 'region',
            'aria-label': labels.quotedBody,
          }
          return ['div', attrs, 0]
        },
      },
    },

    /**
     * 답장 layout 한 번에 삽입.
     *
     * 4-region 모두를 doc content 로 한 번에 교체한다 (PoC 와 동일 시맨틱).
     * NgwEditorMailExtensions / NgwEditorMailRegion 의 set/remove 는 본 명령이 아닌 별도 PM
     * transaction 으로 처리. 본 명령은 quotedButton.install 의 lazy 삽입 경로에서도 사용.
     *
     * 인자 형태:
     *   args[0] = {
     *     nbody?: string; signature?: string;
     *     quotedHeader?: string; quotedBody?: string;
     *   }
     * (각 region 의 HTML 단편)
     */
    commands: {
      insertMailLayout: (state, dispatch, _view, ...args) => {
        if (dispatch == null) return true

        const layoutArg = args[0]
        const layout =
          layoutArg != null && typeof layoutArg === 'object'
            ? (layoutArg as {
                nbody?: string
                signature?: string
                quotedHeader?: string
                quotedBody?: string
                /** 인용문 초기 접힘 상태. 미지정 시 true(접힘). OMD-104: showOriginalMessage 로 제어. */
                quotedCollapsed?: boolean
              })
            : {}
        // 인용문 초기 fold 상태 — header/body 가 같은 값이어야 wrapper fold 클래스가 동기된다.
        const quotedCollapsed = layout.quotedCollapsed ?? true

        const { schema } = state
        let tr = state.tr
        const parser = PMDOMParser.fromSchema(schema)

        const buildRegion = (
          nodeName: string,
          html: string,
          attrs: Record<string, unknown> = {},
        ) => {
          const nodeType = schema.nodes[nodeName]
          if (nodeType == null) return null
          const container = htmlToContainer(html)
          const parsed = parser.parse(container)
          // A-1.fix (ngwEditor-migration-0.2.12): doc.content 강제(`nbody blank_sign? ...`)로
          // PMDOMParser.parse 가 외부 paragraph 를 doc 시퀀스 만족용으로 nbody 안에 자동 wrap.
          // 결과: parsed.content = Fragment(nbody(paragraph)) → region 안에 nbody nested.
          // 단일 nbody wrap 만 unwrap (이미 외부 HTML 이 nbody div 라면 의도된 구조라 보존).
          // nested region 을 펼쳐 순수 block 시퀀스로 만든다 — 4-region 구조로 저장된 메일을
          // 인용할 때 quoted_body 안에 nbody/blank_sign 등이 들어가 스키마(`block+`)를 위반하고
          // 이후 토글이 크래시하는 회귀 차단(flattenNestedRegions 주석 참조).
          const blocks = flattenNestedRegions(unwrapAutoNbody(parsed.content))
          return blocks.length > 0 ? nodeType.create(attrs, blocks) : nodeType.create(attrs)
        }

        const blocks: ReturnType<typeof buildRegion>[] = []
        const nbodyNode = buildRegion('nbody', layout.nbody ?? '<p>본문</p>')
        if (nbodyNode != null) blocks.push(nbodyNode)
        if (layout.signature !== undefined) {
          // 6-region: signature 앞에 blank_sign 빈 줄 자동 삽입 (nbody 와 signature 사이 여백).
          // 빈 문단은 반드시 `<p></p>` (명시적 `<br>` 없음). `<p><br></p>` 로 넣으면 PM EditorView 가
          // 커서 표시용 trailingBreak `<br>` 를 추가로 부착해 빈 줄이 2 개로 보인다. 로컬
          // htmlToContainer(L152)는 NgwEditorMailRegion 과 달리 stripEmptyBlockTrailingBr 를 거치지
          // 않으므로 리터럴 자체를 빈 문단으로 둔다.
          const blankSignNode = buildRegion('blank_sign', '<p></p>')
          if (blankSignNode != null) blocks.push(blankSignNode)
          const sigNode = buildRegion('signature', layout.signature)
          if (sigNode != null) blocks.push(sigNode)
        }
        if (layout.quotedHeader !== undefined || layout.quotedBody !== undefined) {
          // 6-region: quoted 영역 앞에 blank_msg 빈 줄 1개 자동 삽입 (header/body 중 하나라도 있으면).
          // 빈 문단 `<p></p>` 사용 이유는 위 blank_sign 주석 참조 (trailingBreak 중복 → 빈 줄 2개 방지).
          const blankMsgNode = buildRegion('blank_msg', '<p></p>')
          if (blankMsgNode != null) blocks.push(blankMsgNode)
        }
        if (layout.quotedHeader !== undefined) {
          // quoted_body 와 동기된 초기 fold 상태로 생성 — 외곽 토글이 두 노드를 한 transaction
          // 에 토글하므로 시작 상태도 일치해야 quoted_header_editor wrapper 에 fold 클래스가
          // 같이 부여된다.
          const qhNode = buildRegion('quoted_header', layout.quotedHeader, {
            collapsed: quotedCollapsed,
          })
          if (qhNode != null) blocks.push(qhNode)
        }
        if (layout.quotedBody !== undefined) {
          const qbNode = buildRegion('quoted_body', layout.quotedBody, {
            collapsed: quotedCollapsed,
          })
          if (qbNode != null) blocks.push(qbNode)
        }

        const validBlocks = blocks.filter(
          (b): b is NonNullable<typeof b> => b !== null && b !== undefined,
        )
        if (validBlocks.length === 0) return false

        tr = tr.replaceWith(0, state.doc.content.size, validBlocks)
        dispatch(tr.scrollIntoView())
        return true
      },
    },

    // A-3 (ngwEditor-migration-0.2.12): appendTransaction Plugin 안전망 — Cmd+A → Delete /
    // setHTML 회귀 / programmatic 변경 등 키보드 외 경로로 nbody 가 사라지면 자동 재삽입.
    //
    // PoC P-PM-RUNTIME (옵션 (d), 2026-05-28) 채택 결과:
    //   - @ngweditor/react 는 `Plugin` 클래스를 runtime export 하지 않음
    //   - 옵션 (a) prosemirror-state standalone 은 PM 이 bundle inline 되어 createKey 카운터 충돌
    //   - 채택: bundle 내부 Plugin constructor 를 runtime 에 추출 + setup 콜백 안에서 주입
    //   - 호환 근거: Configuration class (bundle 라인 6824-6832) 가 duck-typing 으로
    //     plugin.key/plugin.spec.* 만 검사, bundle 전체에 instanceof Plugin 체크 0건
    //
    // 주의 (Cycle 7 timing 가이드):
    //   - BundledPlugin 추출은 view 생성 후 (`setup` 콜백 안). `getPlugins` 는 preparePlugins
    //     단계라 view 생성 전이라 editor.view 접근 불가.
    //   - view.updateState(reconfigure) 는 dispatchTransaction hook 미경유 → onChange 미발화.
    //     Plugin 주입 자체는 doc 변경이 없으므로 dirty 트리거 안 함이 의도된 동작.
    setup(ctx) {
      const view = ctx.editor.view
      const firstPlugin = view.state.plugins[0]
      const BundledPlugin = firstPlugin?.constructor as
        | (new (spec: BundledPluginSpec) => Plugin)
        | undefined
      if (BundledPlugin == null) {
        if (isDev()) {
          console.warn(
            '[mail-regions] BundledPlugin 추출 실패 (plugins[0] === undefined) — ' +
              'appendTransaction 안전망 비활성. A-1 (doc content 강제) + A-2 (isolating) 만으로 ' +
              '키보드 시나리오 보호 유지. design.md §2 A-3 fallback 참조.',
          )
        }
        return
      }

      const nbodyGuardPlugin = new BundledPlugin({
        appendTransaction(trs, _oldState, newState) {
          if (!trs.some((tr) => tr.docChanged)) return null
          const first = newState.doc.content.firstChild
          if (first != null && first.type.name === 'nbody') return null
          const nbody = newState.schema.nodes.nbody?.createAndFill()
          if (nbody == null) return null
          // A-1 doc content 강제가 미적용 또는 edge case 로 우회된 상태에서 doc 가 비-region
          // 노드 (paragraph 등) 를 firstChild 로 들고 있을 수 있다. insert 만으로는 새 nbody +
          // 기존 노드가 공존하는 schema 위반 doc 가 생성될 수 있어 doc.content 전체를 nbody 로
          // 교체. (design.md §2 A-3 분석 — Cycle 6 발견)
          return newState.tr.replaceWith(0, newState.doc.content.size, nbody)
        },
      })

      // C-1 (editor-collapsed-region-guard): 접힌 영역 진입 차단 + 커서 복귀. nbody 안전망과
      // 관심사 분리를 위해 별도 plugin 으로 주입(design.md 옵션 A). 로직은 export 순수 함수라
      // 단위 테스트로 검증(mailRegions-collapsedGuard.test.ts).
      const collapsedGuardPlugin = new BundledPlugin({
        filterTransaction: (tr) => collapsedGuardFilterTransaction(tr),
        appendTransaction: (trs, oldState, newState) =>
          collapsedGuardAppendTransaction(trs, oldState, newState),
      })

      // D-1 (region-clipboard-unwrap): 클립보드 slice 의 최상위 region 노드 평탄화.
      // copy 측(transformCopied)은 자기 출력 정규화 — 직렬화 HTML 마커와 data-pm-slice context
      // 양쪽에서 region 을 제거. dragstart 도 serializeForClipboard 를 거치므로 내부 drag&drop
      // 경로까지 함께 커버. paste 측(transformPasted)은 외부 유입(수정 전 클립보드/뷰어 복사
      // HTML) 방어. 로직은 export 순수 함수 unwrapRegionSlice — 단위 테스트로 검증.
      const clipboardRegionPlugin = new BundledPlugin({
        props: {
          transformCopied: (slice) => unwrapRegionSlice(slice),
          transformPasted: (slice) => unwrapRegionSlice(slice),
        },
      })

      view.updateState(
        view.state.reconfigure({
          // nbody 재삽입(구조 보정) → collapsed 커서 복귀 순서로 appendTransaction 평가.
          plugins: [
            ...view.state.plugins,
            nbodyGuardPlugin,
            collapsedGuardPlugin,
            clipboardRegionPlugin,
          ],
        }),
      )
    },
  }
}

/**
 * bundle 내부 Plugin constructor 시그니처 (duck-typing). A-3 의 `appendTransaction` (nbody 재삽입)
 * 안전망, C-1 의 `filterTransaction` (collapsed 진입 차단), D-1 의 `props` (clipboard slice 변환)
 * 세 용도를 한 spec 으로 표현.
 */
type BundledPluginSpec = {
  // C-1 (editor-collapsed-region-guard): selection-only 트랜잭션 거부용.
  filterTransaction?: (tr: Transaction, state: EditorState) => boolean
  appendTransaction?: (
    transactions: ReadonlyArray<Transaction>,
    oldState: EditorState,
    newState: EditorState,
  ) => Transaction | null
  // D-1 (region-clipboard-unwrap): serializeForClipboard / parseFromClipboard 의 slice hook.
  props?: {
    transformCopied?: (slice: Slice, view: EditorView) => Slice
    transformPasted?: (slice: Slice, view: EditorView) => Slice
  }
}

/**
 * quoted_header NodeView 팩토리.
 *
 * 토글 버튼은 본 NodeView 가 아닌 `.ngw-editor` wrapper 끝에 부착되므로
 * (NgwEditorLifecycle 가 `createQuotedToggleButton` 으로 생성/관리), 본 NodeView 는 단순
 * wrapper + contentDom 만 노출한다.
 *
 * fold 동기화는 자기 attrs.collapsed 를 따라 wrapper 의 `fold` 클래스를 토글한다. 외곽 토글이
 * quoted_header + quoted_body 두 노드의 collapsed attr 를 한 transaction 에 토글하므로
 * update 가 자동 발화하여 fold 클래스도 자동 갱신된다.
 *
 * a11y 라벨(role/aria-label)은 wrapper **생성 시 1회** 설정한다 (OMD-233 잔여 지연 수정).
 * NodeSpec toDOM 의 라벨은 NodeView 가 렌더를 대체해 에디터 DOM 에 닿지 않고, 렌더 후 외부
 * setAttribute(구 syncMailRegionLabels keystroke 경로)는 PM DOMObserver 에 foreign mutation
 * 으로 잡혀 readDOMChange 재파싱 ↔ NodeView 재생성(라벨 소실) 핑퐁을 만든다 — 대용량(MB급)
 * quoted_body 메일에서 키 입력당 ~100ms. `labels` 는 lifecycle 의 live 객체 참조라
 * setMailRegionLabels 이후 생성되는 wrapper 에는 최신 라벨이 자동 반영된다.
 */
export function createQuotedHeaderNodeView(
  labels: MailRegionLabels = DEFAULT_MAIL_REGION_LABELS,
): NodeViewFactory {
  return (node) => {
    const wrapper = document.createElement('div')
    wrapper.className = REGION_CLASSES.quotedHeader
    wrapper.setAttribute('role', 'region')
    wrapper.setAttribute('aria-label', labels.quotedHeader)

    const contentDom = document.createElement('div')
    contentDom.className = 'nine-region-quoted-header-content'
    wrapper.appendChild(contentDom)

    const applyFold = (collapsed: boolean): void => {
      wrapper.classList.toggle('fold', collapsed)
    }
    applyFold(Boolean(node.attrs.collapsed))

    return {
      dom: wrapper,
      contentDOM: contentDom,
      update(updatedNode) {
        if (updatedNode.type.name !== 'quoted_header') return false
        applyFold(Boolean(updatedNode.attrs.collapsed))
        return true
      },
      // locale 변경(syncMailRegionLabels)의 라벨 재기입이 PM 재파싱을 트리거하지 않도록
      // attribute mutation 만 무시한다. quotedBody 의 blanket true 와 달리 범위를 한정해
      // selection 의사-mutation(커서 추적)과 contentDOM childList 판독은 통과시킨다.
      ignoreMutation(mutation) {
        return mutation.type === 'attributes'
      },
    }
  }
}

/**
 * lazy-collapsed-quoted-render 활성 플래그.
 *
 * 접힌(collapsed=true) 상태로 **생성되는** quoted_body NodeView 가 contentDOM 을 노출하지 않아
 * PM 이 인용 자식 DOM 을 아예 구축하지 않게 한다 — reply/draft 재열기에서 region 주입 dispatch
 * 의 지배 비용(2.7MB WebKit 실측: quotedBody.set dispatch 3,991ms, 그중 parse 는 144ms 뿐 =
 * 숨겨진(display:none) 자식 DOM 구축)이 제거된다.
 *
 * 의미 보존 근거:
 *   - 저장/전송(getData·getDataPreprocessed)은 PM doc 기반 직렬화라 view DOM 과 무관.
 *   - 접힘 상태의 표시는 오늘도 display:none(fold CSS) — 숨긴 DOM 과 없는 DOM 은 시각 동일.
 *   - 펼침(collapsed=false 토글) 시 update() 가 false 를 반환해 NodeView 가 **재생성**되고,
 *     그때 full 뷰(contentDOM 포함)로 PM 이 자식을 렌더한다 — 비용은 최초 펼침 1회로 이연.
 *     이후 재접힘/재펼침은 full 뷰의 CSS 토글(기존 동작)이라 즉시.
 *   - "원문 보기" 토글 노출 판정은 quotedBodyNodeHasContent(PM doc 기반)로 전환되어
 *     contentDOM 부재와 무관하게 동작한다(NgwEditorLifecycle·MailExtensions).
 */
export const LAZY_COLLAPSED_QUOTED_RENDER = true

/**
 * quoted_body 에 실제 인용 콘텐츠가 있는지 PM **노드 기반** 판정 — lazy-collapsed 뷰에서는
 * contentDOM 이 없어 DOM 기반 quotedBodyHasContent(textContent/media 쿼리)가 항상 false 가
 * 되므로, 토글 노출/펼침 유지 게이트는 본 함수를 쓴다. DOM 판정과의 대응: 텍스트 ↔
 * textContent, 미디어/위젯 leaf ↔ img·video 등 쿼리(과소 판정으로 토글이 사라지는 쪽이
 * 치명적이라 hard_break/horizontal_rule/page_break 외의 비텍스트 leaf 는 전부 콘텐츠로 본다).
 */
export function quotedBodyNodeHasContent(node: PMNode): boolean {
  // 빈 region(편집 중 lazy 조립/재로딩된 빈 quoted) fast-path — 흔한 케이스를 순회 없이 처리.
  if (node.content.size === 0) return false
  if (node.textContent.trim().length > 0) return true
  let has = false
  node.descendants((child) => {
    if (has) return false
    if (
      child.isLeaf &&
      !child.isText &&
      child.type.name !== 'hard_break' &&
      child.type.name !== 'horizontal_rule' &&
      child.type.name !== 'page_break'
    ) {
      has = true
      return false
    }
    return true
  })
  return has
}

/**
 * quoted_body NodeView 팩토리 — content 의 display 만 담당하도록 단순화 (PR-N7 hotfix — region-layout).
 *
 * 토글 버튼과 sibling-toggle 책임은 quotedHeaderNodeView 로 이전. 본 NodeView 는
 * `data-collapsed` attr 변경 시 자기 content 의 display 만 토글한다.
 *
 * lazy-collapsed-quoted-render: 접힌 상태로 생성되면 contentDOM 없는 경량 뷰를 반환해 자식
 * DOM 구축을 생략하고, 펼침 전환 시 update() false 로 재생성해 full 뷰로 교체한다(위 플래그
 * 주석 참조). 접힘 전환(펼침→접힘)은 full 뷰의 CSS 토글로 처리되어 DOM 을 유지한다.
 *
 * a11y 라벨은 wrapper 생성 시 1회 설정 — 배경은 createQuotedHeaderNodeView 헤더 주석 참조.
 */
export function createQuotedBodyNodeView(
  labels: MailRegionLabels = DEFAULT_MAIL_REGION_LABELS,
): NodeViewFactory {
  return (node) => {
    const wrapper = document.createElement('div')
    wrapper.className = REGION_CLASSES.quotedBody
    wrapper.setAttribute('role', 'region')
    wrapper.setAttribute('aria-label', labels.quotedBody)

    if (LAZY_COLLAPSED_QUOTED_RENDER && Boolean(node.attrs.collapsed) === true) {
      // 경량(lazy) 뷰 — contentDOM 미노출로 PM 이 인용 자식 DOM 을 만들지 않는다. 접힘 상태의
      // 시각 결과는 기존(fold display:none)과 동일(아무것도 안 보임). 내용 편집은 접힘 상태에선
      // 원래 불가능하므로 contenteditable 상호작용 손실도 없다.
      wrapper.classList.add('fold')
      return {
        dom: wrapper,
        update(updatedNode) {
          if (updatedNode.type.name !== 'quoted_body') return false
          // 펼침 전환 → false 반환으로 재생성(full 뷰) 유도. PM 이 그 시점에 자식을 렌더한다.
          // content 변경은 doc 모델에만 반영되면 충분(뷰는 자식을 렌더하지 않음)하므로 수용.
          return Boolean(updatedNode.attrs.collapsed) === true
        },
        ignoreMutation() {
          return true
        },
      }
    }

    const contentDom = document.createElement('div')
    contentDom.className = 'nine-quoted-content'
    wrapper.appendChild(contentDom)

    let collapsed = Boolean(node.attrs.collapsed)

    const updateUI = () => {
      // fold 클래스 일원화 — CSS `.quoted_body_editor.fold > .nine-quoted-content` 가 display 처리.
      wrapper.classList.toggle('fold', collapsed)
    }
    updateUI()

    return {
      dom: wrapper,
      contentDOM: contentDom,
      update(updatedNode) {
        if (updatedNode.type.name !== 'quoted_body') return false
        collapsed = Boolean(updatedNode.attrs.collapsed)
        updateUI()
        return true
      },
      ignoreMutation() {
        return true
      },
    }
  }
}

/**
 * 4-region 의 시각 구분 CSS.
 * 답장 HTML 의 setHTML → getHTML 라운드트립 시 본 클래스가 유지된다.
 *
 * 본 상수는 PoC 페이지의 `<style>` 직접 주입 + `ensureMailRegionStyles()` 멱등 주입의 단일 source.
 * fold(`collapsed=true`) 일 때 본문 표시 토글이 본 CSS 의 `.quoted_body_editor.fold > .nine-quoted-content`
 * 규칙에 의존하므로 NodeView 가 사용되는 모든 환경에서 본 스타일이 document 에 존재해야 한다.
 */
export const mailRegionStyles = `
  .nine_body, .nine_signature, .quoted_header_editor, .quoted_body_editor {
    position: relative;
  }
  .nine_body {
    background: transparent;
  }
  /* toolbar 배경 수정 — .ngw-editor-container 로 스코프 한정(에디터 외부의 role=toolbar,
     예: ImageToolbar 에 누수 방지). specificity 0,2,0 이라 라이브러리 Tailwind 유틸리티
     (bg-gray-50 / rounded-t-xl, @scope 내 0,1,0)를 !important 없이 override. */
  .ngw-editor-container [role="toolbar"] {
    background-color: transparent;
    border-radius: 0;
    border-top: solid 1px #e5e7eb;
    border-bottom: solid 1px #e5e7eb;
    margin: 6px 0;
  }
  /* 라이브러리 default '.ngw-editor .ProseMirror > * + *' { margin-top: 0.75em } 의 region 간
     간격을 제거 (specificity 0,3,0 > 0,2,0 로 override). region 사이 여백은 blank_sign /
     blank_msg 빈 줄 region 이 담당. */
  .ngw-editor .ProseMirror > .nine_body,
  .ngw-editor .ProseMirror > .blank.sign,
  .ngw-editor .ProseMirror > .blank.msg,
  .ngw-editor .ProseMirror > .nine_signature,
  .ngw-editor .ProseMirror > .quoted_header_editor,
  .ngw-editor .ProseMirror > .quoted_body_editor {
    margin-top: 0;
  }
  .ngw-editor p {
    margin: 0;
  }
  /* li 태그가 editor에서만 margin이 크게 적용됨. 브라우저 기본 스타일과 동일하게 margin 제거. - 나중에 에디터팀에 전달해서 반영 필요. */
  .ngw-editor li {
    margin: 0;
  }
  /* .ngw-editor:focus-within 시 지정되는 border-color 제거. */
  .ngw-editor:focus-within {
    border-color: transparent !important;
  }
  /* blank_sign / blank_msg — 본문/서명/인용 사이 빈 줄 region. 자동 spacer(<div class="blank
     sign"><br></div>) 를 정식 노드로 승격한 것으로, 빈 줄 1줄 높이를 보장한다. */
  .blank.sign, .blank.msg {
    min-height: 1em;
  }
  .quoted_header_editor p {
    margin: 0;
  }
  /* 원본 메일의 quoted_header 안 hr 는 inline style (border/height/background) 을 그대로
     보존해야 한다. 라이브러리 default '.ngw-editor hr' 가 max-width: 600px / margin: 20px auto /
     background: none 으로 시각을 바꾸지 못하도록 좁은 영역에서 reset 한다.
     - rawStyle 보존(preserveHtml=true) 경로가 정상이면 hr 의 inline style 이 specificity 로
       우리 stylesheet 룰을 이긴다 — fallback (background-color / height) 은 inline 에 의해 덮임.
     - rawStyle 미보존 회귀 시에는 본 룰의 fallback 시각이 1px 회색 줄로 hr 을 복구. */
  .ngw-editor .quoted_header_editor hr {
    max-width: none;
    margin: 0;
    padding: 0;
    appearance: none;
    cursor: default;
    background-color: #e1e1e1;
    height: 1px;
  }
  .quoted_body_editor {
    padding-left: 0;
  }
  /* fold = collapsed (NodeView 의 wrapper.classList.toggle('fold', collapsed) 와 toDOM 의
     fold 클래스 출력이 본 셀렉터로 처리됨 — display 직접 조작 없음). */
  .quoted_body_editor.fold {
    display: none;
  }
  .quoted_header_editor.fold {
    display: none;
  }
  .nine-quoted-toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 48px;
    padding: 4px 12px;
    margin: 4px 0 4px 8px;
    border: 1px solid #d1d5db;
    border-radius: 12px;
    background: #f5f5f5;
    color: #6b7280;
    font-size: 14px;
    font-weight: bold;
    letter-spacing: 2px;
    cursor: pointer;
    user-select: none;
    transition: all 0.15s ease;
  }
  /* '.ngw-editor' wrapper 의 마지막 자식으로 부착될 때 (block 흐름 하단) — 좌측 정렬 + 상하 여백. */
  .nine-quoted-toggle--block {
    display: flex;
    margin: 8px 0 0 0;
    align-self: flex-start;
  }
  .nine-quoted-toggle:hover {
    background: #e5e7eb;
    border-color: #9ca3af;
    color: #111827;
  }
  /* 한번 펼쳐지면 다시 닫을 수 없음. */
  .nine-quoted-toggle--expanded {
    display: none;
    letter-spacing: 0;
    font-weight: normal;
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 8px;
  }
`

/**
 * `getDataPreprocessed` 의 최종 출력을 spec §1 구조로 감싼다.
 * docs/editor/mail-body-structure.md 의 §1, §5.1 의 단일 source 구현.
 *
 * 동작:
 *   1. innerHTML 안에서 4 region 노드(nine_body / nine_signature / quoted_header_editor /
 *      quoted_body_editor) 가 하나라도 존재하면 spec wrapper 로 재조립.
 *   2. region 이 하나도 없으면 입력을 그대로 반환 — SignatureEdit 등 4-region 모델을
 *      쓰지 않는 에디터 호출 경로에서 부작용 없이 동작.
 *   3. 입력이 이미 `<div nine-mail-id>` wrapper 로 감싸진 경우 그대로 반환 — 멱등.
 *
 * 재조립 규칙(spec §1):
 *   - `<!--nine-body-->` 주석을 nine_body 직전에 삽입.
 *   - nine_body 자식 순서: `[본문 paragraphs, blank_sign?, signature?, blank_msg?]`.
 *     - blank_sign / signature 가 nine_body 의 형제로 들어왔다면 nine_body 의 자식으로 이동.
 *     - blank_msg 도 nine_body 의 자식 (signature 뒤). 단 blank_msg 는 quoted_header /
 *       quoted_body 가 있을 때만 존재 — quoted 부재 시 blank_msg 도 부재 (spec §2.4).
 *   - quoted_header / quoted_body 는 nine_body 의 형제로 유지.
 *   - `<!--quoted-header-->` 주석을 quoted_header 직전에 삽입.
 *   - 외곽을 `<div nine-mail-id="{nineMailId}">` 로 감싼다.
 */
/**
 * `wrapEmailBodyHTML` 의 외곽 형태 옵션.
 *
 * - 미지정: 기존 동작 (`<div nine-mail-id="...">...</div>`).
 * - `document: {...}`: full HTML document (`<!DOCTYPE html><html><head><meta...></head>
 *   <body style="..." nine-mail-id="..." dir="...">...</body></html>`). sample/test1.html 의
 *   외곽 형태와 동등. body 에 nine-mail-id 가 부착되어 받는 메일 클라이언트가 표준 HTML
 *   document 로 렌더링하도록 한다.
 */
export interface WrapEmailBodyOptions {
  document?: {
    bodyStyle?: string
    dir?: string
  }
}

const escapeHtmlAttr = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

export function wrapEmailBodyHTML(
  nineMailId: string,
  innerHTML: string,
  options?: WrapEmailBodyOptions,
): string {
  if (typeof document === 'undefined') return innerHTML
  if (!nineMailId) return innerHTML

  const doc = new DOMParser().parseFromString(`<div>${innerHTML}</div>`, 'text/html')
  const source = doc.body.firstElementChild
  if (source == null) return innerHTML

  // 멱등 — 이미 wrap 된 입력은 그대로 반환 (div / body 양쪽).
  // region (quoted_body 등) 에 부착된 nine-mail-id 는 외곽 wrapper 가 아니므로 제외한다.
  const existingDivWrapper = Array.from(source.querySelectorAll('div[nine-mail-id]')).find(
    (el) => !isRegionDiv(el),
  )
  if (existingDivWrapper != null) return innerHTML
  if (/<body\b[^>]*\bnine-mail-id\s*=/i.test(innerHTML)) return innerHTML

  const nineBody = source.querySelector(
    'div.nine_body, div[id^="nine_body_"]',
  ) as HTMLElement | null
  const blankSign = source.querySelector(
    'div.blank.sign, div[id^="blank_sign_"]',
  ) as HTMLElement | null
  const signature = source.querySelector(
    'div.nine_signature, div[id^="nine-sign-"]',
  ) as HTMLElement | null
  const blankMsg = source.querySelector(
    'div.blank.msg, div[id^="blank_msg_"]',
  ) as HTMLElement | null
  const quotedHeader = source.querySelector(
    'div.quoted_header_editor, div[id^="quoted_header_"]',
  ) as HTMLElement | null
  const quotedBody = source.querySelector(
    'div.quoted_body_editor, div[id^="quoted_body_"]',
  ) as HTMLElement | null

  if (
    nineBody == null &&
    blankSign == null &&
    signature == null &&
    blankMsg == null &&
    quotedHeader == null &&
    quotedBody == null
  ) {
    return innerHTML
  }

  const wrapper = doc.createElement('div')
  wrapper.setAttribute('nine-mail-id', nineMailId)

  // nine_body 결정: 명시적 nine_body 노드가 있으면 그대로, 없으면 region 이 아닌 자식들을
  // 모아 새 nine_body 컨테이너로 합성. EmailForm 의 본문 흐름은 `insertMailLayout` 을 거치지
  // 않고 PM paragraph 노드들을 직접 사용하므로 직렬화 결과는 평탄 형태가 기본이다.
  // isRegionDiv 가 blank_sign/blank_msg 도 region 으로 인식(S1)하므로 본문 합성에서 제외된다.
  let bodyContainer: HTMLElement | null = nineBody
  if (bodyContainer == null) {
    const bodyChildren: Node[] = []
    for (const c of Array.from(source.childNodes)) {
      if (c.nodeType === Node.COMMENT_NODE) continue
      if (c.nodeType === Node.ELEMENT_NODE && isRegionDiv(c as Element)) continue
      bodyChildren.push(c)
    }
    if (bodyChildren.length > 0) {
      bodyContainer = doc.createElement('div')
      bodyContainer.className = 'nine_body'
      bodyContainer.id = `nine_body_${nineMailId}`
      for (const c of bodyChildren) bodyContainer.appendChild(c)
    }
  }

  // 송신 HTML 출력 구조: blank_sign + nine_signature + blank_msg 는 nine_body 의 **자식**으로
  // nesting (순서 유지). quoted_header / quoted_body 는 nine_body 의 **형제**. blank_msg 는
  // quoted_header/quoted_body 가 있을 때만 존재하므로 (spec §2.4) bodyContainer 부재 분기에서는
  // 동반 가능성이 없다. PM doc 내부는 형제 6-region 을 유지하며, 본 출력 단계에서만 nesting 한다
  // — setData 의 unwrapEmailBodyHTML 이 nine_body 안의 blank_sign / signature / blank_msg 를 다시
  // 형제로 끌어올려 정규화하므로 라운드트립은 멱등.
  if (bodyContainer != null) {
    if (blankSign != null) bodyContainer.appendChild(blankSign)
    if (signature != null) bodyContainer.appendChild(signature)
    if (blankMsg != null) bodyContainer.appendChild(blankMsg)
    wrapper.appendChild(doc.createComment('nine-body'))
    wrapper.appendChild(bodyContainer)
  } else {
    // 본문(nine_body) 부재 + signature 단독 — 빈 nine_body 를 만들지 않고 형제로 보존.
    // blank_msg 는 spec §2.4 상 nine_body 가 있어야만 동반되므로 본 분기엔 들어오지 않는다.
    if (blankSign != null) wrapper.appendChild(blankSign)
    if (signature != null) wrapper.appendChild(signature)
  }
  if (quotedHeader != null) {
    wrapper.appendChild(doc.createComment('quoted-header'))
    wrapper.appendChild(quotedHeader)
  }
  if (quotedBody != null) {
    wrapper.appendChild(quotedBody)
  }

  // document 외곽 모드 — `<!DOCTYPE html><html><head>...</head><body nine-mail-id ...>{regions}</body></html>`
  // 형태. wrapper 의 자식들 (주석 마커 + region div + spacer) 만 body 안에 들어간다 (외곽 div 는
  // 풀어서 body 가 곧 그 역할을 한다).
  if (options?.document != null) {
    // OMD-39: body 로 갈 `<style>` 을 `<head>` 로 격리한다. text/plain 생성기(`convert`)·서버 preview
    // 추출기가 `<body>` 만 파싱하므로, 본문 최상단에 재주입되는 `<style>`(에디터 base-reset 등) 이
    // text/plain·preview 에 CSS 텍스트로 새는 문제를 원천 차단한다(실메일 Ma5ab… 로 확인).
    // - 순서 보존: querySelectorAll 은 문서 등장 순서 → 그대로 append 해 cascade 를 유지한다.
    // - `@scope` 제외: 특히 prelude 없는 `@scope { }` 는 부모를 암묵 scope root 로 삼아 head 이동 시
    //   스코프가 깨진다. 위치 의존 규칙은 body 에 남긴다(현재 재주입 CSS 엔 없으나 외부 유입 방어).
    // - `<style>` 은 head/body 어디에 있든 문서 전체에 적용되므로 렌더 결과는 불변(위치 독립).
    const movedStyleHtml: string[] = []
    for (const styleEl of Array.from(wrapper.querySelectorAll('style'))) {
      if (/@scope\b/i.test(styleEl.textContent ?? '')) continue
      movedStyleHtml.push(styleEl.outerHTML)
      styleEl.remove()
    }
    const headStyles = movedStyleHtml.length > 0 ? `${movedStyleHtml.join('\n')}\n` : ''

    const innerParts: string[] = []
    for (const child of Array.from(wrapper.childNodes)) {
      if (child.nodeType === Node.COMMENT_NODE) {
        innerParts.push(`<!--${(child as Comment).data}-->`)
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        innerParts.push((child as Element).outerHTML)
      } else if (child.nodeType === Node.TEXT_NODE) {
        innerParts.push((child as Text).data)
      }
    }
    const bodyInner = innerParts.join('\n')
    const styleAttr =
      options.document.bodyStyle != null && options.document.bodyStyle.length > 0
        ? ` style="${escapeHtmlAttr(options.document.bodyStyle)}"`
        : ''
    const dir = options.document.dir ?? 'auto'
    return (
      `<!DOCTYPE html>\n` +
      `<html>\n` +
      `<head>\n` +
      `<meta http-equiv="Content-Type" content="text/html; charset=utf-8" />\n` +
      headStyles +
      `</head>\n` +
      `<body${styleAttr} nine-mail-id="${escapeHtmlAttr(nineMailId)}" dir="${escapeHtmlAttr(dir)}">\n` +
      `${bodyInner}\n` +
      `</body>\n` +
      `</html>`
    )
  }

  return wrapper.outerHTML
}

/**
 * setData 입력에서 blank 영역(빈 줄)을 `<div class="blank sign|msg">` wrapper 로 정규화한다
 * (6-region). PM parseDOM 의 `div.blank.sign` / `div.blank.msg` rule 이 이 wrapper 를 blank_sign /
 * blank_msg 노드로 인식한다.
 *
 * 흡수 규칙 (anchor = signature 또는 quoted_header/body 기준):
 *   - A) anchor 직전 형제가 `div.blank.{kind}` → 정규형. className 정규화만 (nine-pg 등 부가 토큰 제거).
 *   - B) 같은 parent 안 anchor 앞쪽에 `div.blank.{kind}` 존재 → 원본 div 를 wrapper 로 재사용,
 *        그 다음 형제부터 anchor 직전까지를 그 안으로 흡수.
 *   - C) 그 외 → no-op (blank 없음).
 *
 * 흡수 후 blank 가 element 자식이 없으면 `<p><br></p>` 1개 보충 (PM `block+` 최소 요건).
 */
function absorbBlankRegion(
  doc: Document,
  source: Element,
  anchorSelector: string,
  kind: 'sign' | 'msg',
): void {
  const anchor = source.querySelector(anchorSelector)
  if (anchor == null) return
  const parent = anchor.parentElement
  if (parent == null) return

  const ensureContent = (blank: Element): void => {
    if (blank.childElementCount === 0) {
      const p = doc.createElement('p')
      p.appendChild(doc.createElement('br'))
      blank.appendChild(p)
    }
  }

  // 출력용 className — legacy/모바일 호환을 위한 nine-pg 토큰 prepend.
  const outputClass = kind === 'sign' ? BLANK_SIGN_OUTPUT_CLASS : BLANK_MSG_OUTPUT_CLASS

  const prev = anchor.previousElementSibling
  // A) 바로 앞이 div.blank.<kind> — 정규형. className 정규화만.
  if (prev != null && prev.matches(`div.blank.${kind}`)) {
    prev.className = outputClass
    ensureContent(prev)
    return
  }
  // B) 같은 parent 앞쪽의 div.blank.<kind> 를 원본 재사용 — 그 다음 형제부터 anchor 직전까지 흡수.
  const blank = Array.from(parent.children).find(
    (el) =>
      el.matches(`div.blank.${kind}`) &&
      (el.compareDocumentPosition(anchor) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0,
  )
  if (blank != null) {
    blank.className = outputClass
    let cur = blank.nextElementSibling
    while (cur != null && cur !== anchor) {
      const next = cur.nextElementSibling
      blank.appendChild(cur)
      cur = next
    }
    ensureContent(blank)
    return
  }
  // C) blank 없음 — no-op
}

/**
 * `nine_body` 의 직계 자식으로 `div.blank.sign` 이 2개 이상 들어온 경우를 정규화한다.
 *
 * PM schema 의 `doc.content = 'nbody blank_sign? signature? ...'` 가 blank_sign 을 최대 1개로
 * 강제하므로 DOM 정규화 단계에서 미리 1개로 정리해야 schema validation 도 안전하다.
 *
 * 정책 (시나리오별):
 *   - 인접 blank.sign 들을 nextElementSibling 으로 연결된 **그룹** 으로 묶는다.
 *   - signature 존재 + signature 직전 그룹이 있으면: **그 그룹이 keeper** — 그룹 내 첫 element
 *     에 나머지 element 의 자식을 흡수, className 을 `blank sign` 으로 정규화.
 *   - signature 부재: **마지막 그룹이 keeper** (서명 재활용을 위해 보존).
 *   - keeper 가 아닌 그룹의 element 는 wrapper 만 제거하여 자식을 nine_body 의 일반 자식으로
 *     풀어낸다 (사용자가 본문 사이 빈 줄로 의도한 케이스 보존).
 *   - signature 가 있는데 어떤 그룹도 signature 직전이 아니면 모든 그룹 unwrap.
 *
 * 적용 범위: nine_body 의 직계 자식만. 다른 region (quoted_body 등) 안의 div.blank.sign 은 무관.
 *
 * 호출 시점: `unwrapEmailBodyHTML` 의 끌어올림 단계 직후, `absorbBlankRegion('sign')` 호출 직전.
 * 후속 absorbBlankRegion 의 A 분기가 keeper 의 ensureContent / className 잔여 처리를 담당.
 */
/**
 * `<div class="blank sign">` 의 자식이 의미 있는 content 없이 빈 줄 표식 (`<br>` 또는 빈 `<p>`)
 * 뿐인지 판정. unwrap 그룹 처리에서 빈 wrapper 를 제거할지 결정하는 기준.
 *
 * 빈 wrapper 의 자식 `<br>` 를 nine_body 의 자식으로 풀어내면 PM parser 가 paragraph 자동
 * wrapping + cursor 표시용 `ProseMirror-trailingBreak` 추가로 빈 줄이 2 개로 표시되는 회귀가
 * 발생한다 — legacy 4-region 의 `<div class="nine-pg blank sign"><br></div>` 같은 spacer 가
 * 그대로 unwrap 되면 문제.
 *
 * 판정 기준: text content 가 비어 있고 (whitespace 만), inline media (img/video 등) 도 없음.
 */
function isEmptyBlankWrapper(el: Element): boolean {
  if (el.textContent != null && el.textContent.trim().length > 0) return false
  if (el.querySelector('img, video, audio, iframe, canvas, svg, object, embed') != null) {
    return false
  }
  return true
}

/**
 * `<div class="blank sign|msg">` 의 자식 중 의미 있는 content 없이 빈 줄 표식 (`<br>`, 빈 `<div>`,
 * 빈 `<p>` 등) 만 있는 자식들을 모두 제거한다.
 *
 * 배경: legacy 4-region 의 spacer (`<div class="nine-pg" style="margin: 0"><br></div>` 또는
 * 자체 `<br>`) 가 blank wrapper 의 자식으로 들어오는 경우, PM parser 가 각 자식을 paragraph 로
 * wrap 하여 EditorView 의 cursor 표시용 `<br class="ProseMirror-trailingBreak">` 가 자식 수만큼
 * 추가되어 빈 줄이 여러 개로 보인다.
 *
 * 정책: blank wrapper 의 자식 중 `isEmptyBlankWrapper` 인 것은 모두 제거. 사용자가 의도적으로
 * 입력한 텍스트/미디어 자식은 보존. 모든 자식이 비어있어 element 가 빈 상태가 되면 PM 의
 * `blank_sign[block+]` schema 의 fillContent 가 빈 paragraph 1 개를 자동 보충 →
 * EditorView 가 trailingBreak 1 개만 부착 → 시각적 빈 줄 1 개 (정상 cursor 표시).
 */
function cleanupEmptyChildrenInBlankRegion(blankEl: Element): void {
  for (const child of Array.from(blankEl.children)) {
    // <p> 는 PM 의 paragraph schema 정식 출력. 빈 paragraph (`<p></p>`) 또는 `<p><br></p>`
    // 는 PM 이 인식할 수 있는 정상 형태이므로 cleanup 대상에서 제외 (라운드트립 멱등성 보장).
    // setData pipeline 마지막의 stripEmptyBlockTrailingBr 가 `<p><br></p>` → `<p></p>` 정규화
    // 후 PM 이 trailingBreak 1 개만 부착.
    if (child.tagName === 'P') continue
    // 그 외 (`<br>` 단독, `<div class="nine-pg"><br></div>` 같은 legacy spacer) 만 제거.
    if (isEmptyBlankWrapper(child)) {
      child.remove()
    }
  }
  // 모든 자식이 제거되어 blank wrapper 가 빈 element 가 되면 빈 paragraph 1 개를 명시 보충한다.
  // 미 보충 시 PM 의 schema fillContent 가 안 작동하는 경로에서 blank wrapper 가 시각적으로
  // 사라지는 회귀가 발생할 수 있다. `<p></p>` 가 들어가면 EditorView 가 cursor 표시용
  // trailingBreak 1 개만 부착 → 빈 줄 1 개 (정상 표시).
  if (blankEl.children.length === 0) {
    const doc = blankEl.ownerDocument
    if (doc != null) {
      const p = doc.createElement('p')
      blankEl.appendChild(p)
    }
  }
}

function normalizeMultipleBlankSignsInNineBody(source: Element): void {
  for (const nineBody of Array.from(
    source.querySelectorAll('div.nine_body, div[id^="nine_body_"]'),
  )) {
    const blanks = Array.from(
      nineBody.querySelectorAll(':scope > div.blank.sign, :scope > div[id^="blank_sign_"]'),
    )
    if (blanks.length <= 1) continue

    // DOM 인접 그룹화 — nextElementSibling 으로 연결된 chain 이 같은 그룹.
    const groups: Element[][] = []
    for (const b of blanks) {
      const last = groups[groups.length - 1]
      if (last != null && last[last.length - 1]!.nextElementSibling === b) {
        last.push(b)
      } else {
        groups.push([b])
      }
    }

    // keeper 결정.
    const sig = nineBody.querySelector(
      ':scope > div.nine_signature, :scope > div[id^="nine-sign-"]',
    )
    let keeperGroup: Element[] | null = null
    if (sig != null) {
      // signature 와 인접한 마지막 그룹만 keeper. 어떤 그룹도 signature 직전이 아니면 모두 unwrap.
      const lastGroup = groups[groups.length - 1]!
      if (lastGroup[lastGroup.length - 1]!.nextElementSibling === sig) {
        keeperGroup = lastGroup
      }
    } else {
      // signature 부재 — 마지막 그룹이 keeper (서명 재활용용).
      keeperGroup = groups[groups.length - 1]!
    }

    for (const group of groups) {
      if (group === keeperGroup) {
        const keeper = group[0]!
        for (let i = 1; i < group.length; i++) {
          const groupI = group[i]!
          while (groupI.firstChild != null) {
            keeper.appendChild(groupI.firstChild!)
          }
          groupI.remove()
        }
        keeper.className = BLANK_SIGN_OUTPUT_CLASS
        // keeper 의 자식 중 의미 없는 빈 wrapper (`<br>` 만 또는 빈 `<div>` 등) 들을 제거하여
        // PM 이 각 빈 자식마다 paragraph + trailingBreak 를 추가하지 않도록 한다. 모든 자식이
        // empty 면 PM 의 blank_sign[block+] fillContent 가 빈 paragraph 1 개 자동 보충.
        cleanupEmptyChildrenInBlankRegion(keeper)
      } else {
        // unwrap: 의미 있는 content 가 없는 빈 wrapper 는 자식 풀지 않고 제거.
        // `<div class="nine-pg blank sign"><br></div>` 같은 legacy spacer 의 자식 `<br>` 가
        // nine_body 자식으로 풀려나오면 PM parser 가 paragraph 로 wrapping 후 cursor 표시용
        // `ProseMirror-trailingBreak` 까지 추가하여 빈 줄 2 개로 보이는 회귀를 방지.
        for (const b of group) {
          if (isEmptyBlankWrapper(b)) {
            b.remove()
            continue
          }
          while (b.firstChild != null) {
            nineBody.insertBefore(b.firstChild, b)
          }
          b.remove()
        }
      }
    }
  }
}

/**
 * `setData` 입력 정규화. spec wrapper / 주석 가 PM parser 에 의해 paragraph 로 흡수되는 것을
 * 방지하고, signature 가 nine_body 의 자식인 입력도 형제 형태로 끌어올린다 (PM schema 는 형제
 * 6-region 을 기본 가정). blank 영역은 `absorbBlankRegion` 으로 정규화한다.
 *
 * docs/editor/mail-body-structure.md §5.2 의 라운드트립 허용 변형이 모두 동일한 6-region
 * 노드로 복원되도록 PM parser 전 단계에서 입력 HTML 을 정리.
 *
 * SSR/test 환경(`document` 부재)에서는 입력을 그대로 반환.
 */
export function unwrapEmailBodyHTML(html: string): string {
  if (typeof document === 'undefined') return html
  if (html.length === 0) return html
  // 빠른 path — region 마커가 하나도 없으면 손대지 않는다.
  if (
    !/<!--\s*(nine-body|quoted-header|quoted-body)\s*-->/i.test(html) &&
    !/\bnine-mail-id\s*=/.test(html) &&
    !/\bclass\s*=\s*["'][^"']*\bblank\b\s+(sign|msg)\b/i.test(html) &&
    !/\bclass\s*=\s*["'][^"']*\bnine_signature\b/i.test(html)
  ) {
    return html
  }

  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html')
  const source = doc.body.firstElementChild
  if (source == null) return html

  unwrapEmailBodyHTMLDOM(source)
  return source.innerHTML
}

/**
 * unwrapEmailBodyHTML 의 DOM variant — 문자열 재파싱 없이 이미 파싱된 `source`(본문 컨테이너:
 * Document.body 또는 임시 div) 위에서 직접 정규화한다. setData DOM 통합 경로
 * (draft-setdata-dom-normalize)가 사용하며, 문자열 wrapper 와 동일 로직을 공유해 결과 parity 를
 * 보장한다. `createTreeWalker`/`createElement` 와 absorbBlankRegion 은 `source.ownerDocument` 를
 * 사용한다. (문자열 wrapper 의 fast-path regex 는 DOM 경로에선 생략 — 마커 없는 입력은 자연 no-op.)
 */
export function unwrapEmailBodyHTMLDOM(source: Element): void {
  const doc = source.ownerDocument

  // 외곽 wrapper (`<div nine-mail-id>`) 를 풀어 자식을 끌어올린다.
  //
  // `nine-mail-id` 는 외곽 body wrapper 외에 quoted_body region 에도 부착되므로(인용 원본
  // 메일 ID 보존), region div 는 wrapper 후보에서 제외한다. 제외하지 않으면 외곽 wrapper 가
  // 없는 입력에서 중첩된 quoted_body 가 wrapper 로 오인되어 unwrap 대상이 되는 버그가 생긴다.
  const wrapper = Array.from(source.querySelectorAll('div[nine-mail-id]')).find(
    (el) => !isRegionDiv(el),
  )
  if (wrapper != null) {
    // wrapper 가 source 의 직계가 아닐 수 있다 (입력이 `.ngw-content` 로 한 겹 더 감싸진 경우).
    // replaceWith 로 wrapper 의 실제 위치에 그 자식들을 그대로 펼쳐 부모 관계와 무관하게 처리.
    wrapper.replaceWith(...Array.from(wrapper.childNodes))
  }

  // 모든 HTML 주석 제거 — PM parser 는 주석을 무시하지만 명시 제거가 안전.
  const walker = doc.createTreeWalker(source, NodeFilter.SHOW_COMMENT)
  const comments: ChildNode[] = []
  let current = walker.nextNode()
  while (current != null) {
    comments.push(current as ChildNode)
    current = walker.nextNode()
  }
  for (const c of comments) c.parentNode?.removeChild(c)

  // 다중 blank_sign 정규화 — nine_body 안에 2개 이상의 div.blank.sign 이 들어온 경우를
  // PM schema 의 `blank_sign?` (최대 1개) 제약에 맞게 정리. signature 직전 그룹(또는 signature
  // 부재 시 마지막 그룹)만 keeper 로 통합, 나머지는 wrapper 제거 후 자식 보존. 끌어올림 단계
  // 보다 먼저 수행해야 — 끌어올림이 signature 직전 blank.sign 1개만 분리하므로 helper 의
  // 다중 blank.sign 그룹 인식이 깨진다.
  normalizeMultipleBlankSignsInNineBody(source)

  // nine_body 의 자식인 blank_sign / signature / blank_msg 를 nine_body 의 형제로 끌어올린다.
  // PM schema 는 형제 6-region 을 가정 — 자식으로 두면 nbody.content 의 block 으로 흡수되어
  // region 식별이 깨질 수 있다. 송신 측 wrapEmailBodyHTML 가 nine_body 안에 nesting 한 형태
  // (`nine_body > [본문, blank_sign?, signature?, blank_msg?]`) 와 legacy `nine_body > signature`
  // 전통 형태를 모두 형제 6-region 으로 정규화한다. absorbBlankRegion 후속 호출은 같은 parent
  // 흡수 가정에 의존하므로 이 끌어올림이 선행되어야 한다.
  const BLANK_MSG_SELECTOR = 'div.blank.msg, div[id^="blank_msg_"]'
  for (const nineBody of Array.from(
    source.querySelectorAll('div.nine_body, div[id^="nine_body_"]'),
  )) {
    if (nineBody.parentNode == null) continue
    const sig = nineBody.querySelector('div.nine_signature, div[id^="nine-sign-"]')
    if (sig != null) {
      const innerBlankSign = sig.previousElementSibling
      const innerBlankMsg = sig.nextElementSibling
      // 1) signature 를 nine_body 다음 형제로.
      nineBody.parentNode.insertBefore(sig, nineBody.nextSibling)
      // 2) signature 직전의 blank_sign 도 동반.
      if (innerBlankSign != null && innerBlankSign.matches('div.blank.sign')) {
        nineBody.parentNode.insertBefore(innerBlankSign, sig)
      }
      // 3) signature 직후의 blank_msg 도 동반 (signature 뒤에 위치).
      if (innerBlankMsg != null && innerBlankMsg.matches(BLANK_MSG_SELECTOR)) {
        nineBody.parentNode.insertBefore(innerBlankMsg, sig.nextSibling)
      }
    } else {
      // signature 없이 blank_msg 만 nine_body 자식인 케이스 (서명 부재 + 인용 존재).
      const innerBlankMsg = nineBody.querySelector(BLANK_MSG_SELECTOR)
      if (innerBlankMsg != null) {
        nineBody.parentNode.insertBefore(innerBlankMsg, nineBody.nextSibling)
      }
    }
  }

  // blank 영역 정규화 (6-region). 끌어올리기 이후에 수행해야 signature 가 nine_body 형제로
  // 정규화된 상태에서 같은 parent 흡수가 동작한다.
  absorbBlankRegion(doc, source, 'div.nine_signature, div[id^="nine-sign-"]', 'sign')
  const hasQuotedHeader =
    source.querySelector('div.quoted_header_editor, div[id^="quoted_header_"]') != null
  const quotedAnchorSelector = hasQuotedHeader
    ? 'div.quoted_header_editor, div[id^="quoted_header_"]'
    : 'div.quoted_body_editor, div[id^="quoted_body_"]'
  absorbBlankRegion(doc, source, quotedAnchorSelector, 'msg')

  // 모든 blank.sign / blank.msg wrapper 의 자식 중 의미 없는 빈 wrapper 정리 — 단일 blank
  // 케이스 (normalizeMultipleBlankSignsInNineBody 가 처리하지 않은 경로) 까지 다룬다. PM 의
  // EditorView 가 자식마다 paragraph + trailingBreak 를 추가하는 회귀 방지.
  for (const blank of Array.from(
    source.querySelectorAll(
      'div.blank.sign, div[id^="blank_sign_"], div.blank.msg, div[id^="blank_msg_"]',
    ),
  )) {
    cleanupEmptyChildrenInBlankRegion(blank)
  }

  // 폴백 — `<div class="nine_body">` 가 없지만 다른 region (signature / quoted_*) 이 존재하는
  // 입력은 마이그레이션 이전 본문이거나 외부 시스템에서 받은 비정상 형태. 본문 element 들을
  // 새 `<div class="nine_body">` 로 감싸 PM parseDOM 이 nbody 노드를 정상 생성하도록 한다.
  //
  // 적용 조건이 "다른 region 이 있을 때만" 인 이유 — 4-region 마커가 전혀 없는 평탄 입력
  // (SignatureEdit 의 서명 단독 본문 등) 은 nbody 컨테이너가 어울리지 않는다. unwrap 의 빠른
  // path 가 이미 그 케이스를 손대지 않고 통과시키지만, 본 분기는 추가 안전 장치.
  const hasNineBody = source.querySelector('div.nine_body, div[id^="nine_body_"]') != null
  const otherRegionSelector =
    'div.nine_signature, div[id^="nine-sign-"], ' +
    'div.quoted_header_editor, div[id^="quoted_header_"], ' +
    'div.quoted_body_editor, div[id^="quoted_body_"]'
  const firstOtherRegion = source.querySelector(otherRegionSelector)
  // region 들은 source 직계가 아니라 컨테이너(`.ngw-content` 등) 안에 있을 수 있다. 첫 region 의
  // 실제 부모를 기준으로 본문 자식을 모으고 nbody 를 삽입해야 insertBefore 가 안전하다.
  const regionParent = firstOtherRegion?.parentNode ?? null
  if (!hasNineBody && firstOtherRegion != null && regionParent != null) {
    const bodyChildren: Node[] = []
    for (const c of Array.from(regionParent.childNodes)) {
      if (c.nodeType === Node.COMMENT_NODE) continue
      if (c.nodeType === Node.ELEMENT_NODE && isRegionDiv(c as Element)) continue
      bodyChildren.push(c)
    }
    if (bodyChildren.length > 0) {
      const newNbody = doc.createElement('div')
      newNbody.className = 'nine_body'
      for (const c of bodyChildren) newNbody.appendChild(c)
      regionParent.insertBefore(newNbody, firstOtherRegion)
    }
  }
}

/**
 * quoted_body DOM element 가 실제 인용 콘텐츠를 담고 있는지 판정한다.
 *
 * 판정 기준은 `isEmptyBlankWrapper` 와 동일 — text 가 whitespace 뿐이고 inline media
 * (img/video 등)도 없으면 "빈 인용" 으로 본다.
 *
 * 배경: ngwEditor 엔진은 편집 영역 클릭 등으로 quoted region 을 lazy 하게 doc 에 조립하는데,
 * 실제 인용이 없는 새 메일에서도 **빈** quoted_body 가 생겨 `ensureQuotedToggle` 이 "..."(원문
 * 보기) 토글을 phantom 으로 노출시켰다(임시보관 후 재로딩 시에도 재발). quoted_header/quoted_body
 * 에 실 데이터가 한 번도 담긴 적 없으면 토글을 아예 걸지 않도록, 이 판정을 게이트로 쓴다.
 */
export function quotedBodyHasContent(el: Element): boolean {
  if (el.textContent != null && el.textContent.trim().length > 0) return true
  return el.querySelector('img, video, audio, iframe, canvas, svg, object, embed') != null
}

/**
 * `.ngw-editor` wrapper 끝에 부착하는 "원문 보기 / 숨기기" 토글 버튼을 생성한다.
 *
 * 토글 버튼이 quoted_header NodeView 안이 아닌 에디터 외곽 wrapper 의 자식으로 옮겨진 이유:
 *   - PM contenteditable 영역 바깥에 위치 → selection/입력 흐름과 분리되어 별도 가드 단순화.
 *   - 답장 본문이 길어 quoted_header 가 화면 밖일 때도 토글 접근성 확보.
 *
 * 라이프사이클:
 *   - NgwEditorLifecycle 가 PM doc 에 quoted_body 노드가 존재할 때만 본 helper 로 버튼 생성 후
 *     wrapper 의 마지막 자식으로 appendChild.
 *   - doc 에서 quoted_body 가 사라지면 NgwEditorLifecycle 가 cleanup() 호출 후 remove.
 *
 * 반환:
 *   - `btn`: 버튼 element (appendChild 책임은 호출자)
 *   - `sync()`: 호출 시점의 PM state 로 라벨/fold 클래스를 즉시 갱신 (외부 onChange 에서 호출)
 *   - `cleanup()`: listener 해제 + DOM 분리. dispose 또는 quoted_body 제거 시점에 호출.
 */
export interface QuotedToggleHandle {
  readonly btn: HTMLButtonElement
  readonly sync: () => void
  readonly cleanup: () => void
}

export function createQuotedToggleButton(
  view: EditorView,
  labels: Pick<MailRegionLabels, 'toggleQuoted' | 'hideQuoted'> = DEFAULT_MAIL_REGION_LABELS,
): QuotedToggleHandle {
  const btn = document.createElement('button')
  btn.className = 'nine-quoted-toggle nine-quoted-toggle--block'
  btn.type = 'button'
  btn.contentEditable = 'false'
  btn.draggable = false
  btn.setAttribute('unselectable', 'on')
  btn.setAttribute('aria-label', labels.toggleQuoted)
  btn.textContent = '···'
  btn.title = labels.toggleQuoted

  type RegionTarget = { pos: number; attrs: Record<string, unknown> }
  const findRegion = (nodeName: 'quoted_header' | 'quoted_body'): RegionTarget | null => {
    // region 은 스키마상 doc 직속 자식(≤6개)뿐이다 — region 노드는 'block' group 에서 제외되어
    // (A-1.fix) 다른 region 안에 중첩될 수 없다. descendants 전체 순회(O(doc))는 대용량 본문에서
    // 키 입력당 비용으로 자라므로 depth 0 만 본다 (OMD-233 후속). forEach 의 offset 은 depth 0
    // 에서 descendants 의 pos 와 동일하다.
    let result: RegionTarget | null = null
    view.state.doc.forEach((n, offset) => {
      if (result == null && n.type.name === nodeName) {
        result = { pos: offset, attrs: n.attrs }
      }
    })
    return result
  }

  const sync = (): void => {
    const body = findRegion('quoted_body')
    const collapsed = body == null ? true : Boolean(body.attrs.collapsed)
    btn.setAttribute('aria-label', labels.toggleQuoted)
    btn.title = labels.toggleQuoted
    btn.textContent = collapsed ? '···' : `▲ ${labels.hideQuoted}`
    btn.classList.toggle('nine-quoted-toggle--expanded', !collapsed)
  }

  /**
   * prosemirror-history 의 undo/redo 스택을 완전히 비운다. 라이브러리는 clearHistory 표면을
   * 노출하지 않으므로, 현재 doc/selection 을 보존한 채 EditorState 를 재생성한다 — 각 plugin 의
   * init 이 재실행되며 history plugin 이 빈 스택으로 재초기화되어 undo/redo depth 가 0 이 된다.
   *
   * ⚠ `view.updateState()` 는 EditorCore.handleTransaction(dispatchTransaction) 을 우회하므로,
   * 툴바 undo/redo 버튼 활성 상태를 계산·통지하는 `editorStateChange` 이벤트(undoDepth 기반)가
   * 발화하지 않는다. 그래서 재생성 직후 addToHistory:false no-op 트랜잭션을 한 번 dispatch 해
   * handleTransaction 을 태워 canUndo = undoDepth()>0 = false 로 버튼을 비활성화시킨다.
   *
   * ⚠ EditorState 재생성은 history 외 다른 plugin state 도 재초기화한다(펼치기 전 작성 undo 기록,
   * 진행 중 plugin 상태 손실 감수 — "펼침 후 되돌리기 불가" 요구에 따른 의식적 결정).
   */
  const resetHistory = (): void => {
    const prev = view.state
    // 실제 ProseMirror EditorView 만 plugins 배열과 updateState 를 갖는다. 단위 테스트의 경량
    // stub view(예: `state: { doc, tr }`)에서는 EditorState.create 가 doc.type.schema 부재로
    // 크래시하므로, capability 로 실제 view 를 판별해 stub 환경은 skip 한다(프로덕션 실제 에러는
    // 삼키지 않고 그대로 노출).
    if (!Array.isArray(prev.plugins) || typeof view.updateState !== 'function') return
    // history-only 2단 reconfigure (NgwEditorState.resetUndoHistory 와 동일 패턴 — 0e4e3250b):
    // EditorState.create 전체 재생성은 모든 plugin StateField 재 init 으로 데코레이션 identity
    // 가 바뀌어 거대 문서 전체 redraw(~3.9s/2.7MB WebKit)를 유발한다. history 플러그인만
    // 제거→복원해 done/undone 스택을 재초기화하면 doc/selection/나머지 plugin state 가 동일
    // 인스턴스로 보존되어 redraw 없이 끝난다. 요구사항("펼침 후 되돌리기 불가")은 동일 충족
    // — 기존 구현의 "다른 plugin state 재초기화"는 요구가 아닌 감수했던 부작용이다.
    const historyPlugins = prev.plugins.filter((plugin) => {
      const key = (plugin as { key?: unknown }).key
      return typeof key === 'string' && /^history\$\d*$/.test(key)
    })
    if (historyPlugins.length > 0) {
      const withoutHistory = prev.plugins.filter((plugin) => !historyPlugins.includes(plugin))
      const dropped = prev.reconfigure({ plugins: withoutHistory })
      view.updateState(dropped.reconfigure({ plugins: prev.plugins }))
    } else {
      // 방어 폴백 — history 플러그인을 못 찾으면 기존 전체 재생성 패턴.
      view.updateState(
        EditorState.create({
          doc: prev.doc,
          selection: prev.selection,
          storedMarks: prev.storedMarks ?? undefined,
          plugins: prev.plugins,
        }),
      )
    }
    // 툴바 undo/redo 버튼 상태 재계산 트리거 (기록은 남기지 않음).
    view.dispatch(view.state.tr.setMeta('addToHistory', false))
  }

  /**
   * 외곽 토글이 quoted_header + quoted_body 두 노드의 collapsed attr 를 한 transaction 에
   * 동시 토글. quotedHeaderNodeView 의 update 가 자동 발화하여 fold 클래스가 매끄럽게 갱신된다.
   *
   * setNodeMarkup 이 아닌 setNodeAttribute(AttrStep) 를 쓴다: setNodeMarkup 은 attr 만 바꾸더라도
   * 노드 content 를 스키마(`block+`) 로 재검증하는데, 외부 메일 원문을 파싱한 quoted_body content
   * 는 매처를 완벽히 만족하지 않을 수 있어 "Invalid content for node type quoted_body" RangeError
   * 로 크래시했다. setNodeAttribute 는 content 를 건드리지 않고 attr 만 바꾸므로 이를 회피한다.
   */
  const toggleCollapsed = (): void => {
    const body = findRegion('quoted_body')
    if (body == null) return
    const nextCollapsed = !body.attrs.collapsed
    let tr = view.state.tr
    const header = findRegion('quoted_header')
    if (header != null) {
      tr = tr.setNodeAttribute(header.pos, 'collapsed', nextCollapsed)
    }
    tr = tr.setNodeAttribute(body.pos, 'collapsed', nextCollapsed)
    view.dispatch(tr)
    // 인용을 "펼친" 경우, 펼치기 전 상태로 되돌아갈 수 없도록 history 를 초기화한다. 토글 버튼은
    // 펼침(원문 보기) 이후 `--expanded` 로 숨겨져 재접힘 UI 가 없으므로, 유일한 재접힘 경로인
    // undo/redo 스택을 비워 undo 버튼을 비활성화한다.
    if (!nextCollapsed) {
      resetHistory()
    }
    sync()
  }

  const blockNative = (e: Event): void => {
    e.preventDefault()
    e.stopPropagation()
  }
  const nativeBlockTypes = [
    'selectstart',
    'dragstart',
    'cut',
    'copy',
    'paste',
    'beforeinput',
  ] as const
  for (const type of nativeBlockTypes) {
    btn.addEventListener(type, blockNative)
  }

  const onMouseDown = (e: MouseEvent): void => {
    e.preventDefault()
    e.stopPropagation()
    toggleCollapsed()
  }
  const onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      e.stopPropagation()
      toggleCollapsed()
      return
    }
    e.preventDefault()
    e.stopPropagation()
  }
  btn.addEventListener('mousedown', onMouseDown)
  btn.addEventListener('keydown', onKeyDown)

  sync()

  const cleanup = (): void => {
    for (const type of nativeBlockTypes) {
      btn.removeEventListener(type, blockNative)
    }
    btn.removeEventListener('mousedown', onMouseDown)
    btn.removeEventListener('keydown', onKeyDown)
    btn.parentElement?.removeChild(btn)
  }

  return { btn, sync, cleanup }
}

const MAIL_REGION_STYLE_ELEMENT_ID = 'ngw-mail-region-styles'

/**
 * 4-region CSS 를 document.head 에 멱등 주입.
 *
 * `quotedHeaderNodeView` 의 토글 버튼이 `fold` 클래스를 켜고 끄지만, fold 클래스로 자식 content 를
 * 숨기는 규칙(`.quoted_body_editor.fold > .nine-quoted-content { display: none }`)이 본 CSS 의
 * `mailRegionStyles` 안에만 존재한다. 따라서 NgwEditor 가 사용되는 모든 곳(어댑터/PoC) 에서
 * 한 번씩은 본 함수를 호출해야 토글이 실제 화면에 반영된다.
 *
 * SSR/test 환경(`document` 부재)에서는 NoOp.
 */
export function ensureMailRegionStyles(): void {
  if (typeof document === 'undefined') return
  if (document.getElementById(MAIL_REGION_STYLE_ELEMENT_ID) != null) return
  const style = document.createElement('style')
  style.id = MAIL_REGION_STYLE_ELEMENT_ID
  style.textContent = mailRegionStyles
  document.head.appendChild(style)
}
